import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { navigationConfig } from '../registry';
import { 
  filterNavigationGroups, 
  getAvailableDashboards,
  getDefaultDashboard,
  buildTenantPath,
  extractTenantSlug
} from '../utils';
import type { UserContext, NavigationGroup, NavigationItem } from '../types';

interface UseFilteredNavigationOptions {
  userContext: UserContext;
  preferredDashboardId?: string | null;
}

interface UseFilteredNavigationReturn {
  navigationGroups: NavigationGroup[];
  availableDashboards: NavigationItem[];
  currentDashboard: NavigationItem | undefined;
  defaultDashboard: NavigationItem | undefined;
  tenantSlug: string | undefined;
  buildPath: (path: string) => string;
}

/**
 * Hook to get filtered navigation based on user context
 */
export const useFilteredNavigation = ({
  userContext,
  preferredDashboardId
}: UseFilteredNavigationOptions): UseFilteredNavigationReturn => {
  const location = useLocation();
  
  // Extract tenant slug from URL
  const tenantSlug = useMemo(() => 
    extractTenantSlug(location.pathname) || userContext.tenantSlug,
    [location.pathname, userContext.tenantSlug]
  );

  // Filter navigation groups based on user context
  const navigationGroups = useMemo(() => {
    const groups = filterNavigationGroups(navigationConfig.groups, userContext);
    
    // Update dashboard item in main group with preferred dashboard
    return groups.map(group => {
      if (group.id === 'main' && preferredDashboardId) {
        const preferredDashboard = navigationConfig.dashboards.find(
          d => d.id === preferredDashboardId
        );
        
        if (preferredDashboard) {
          return {
            ...group,
            items: group.items.map(item => {
              if (item.id === 'dashboard-selector') {
                return {
                  ...item,
                  path: preferredDashboard.path,
                  label: preferredDashboard.label,
                  icon: preferredDashboard.icon
                };
              }
              return item;
            })
          };
        }
      }
      return group;
    });
  }, [userContext, preferredDashboardId]);

  // Get available dashboards for user
  const availableDashboards = useMemo(() => 
    getAvailableDashboards(navigationConfig.dashboards, userContext),
    [userContext]
  );

  // Get default dashboard for user role
  const defaultDashboard = useMemo(() => 
    getDefaultDashboard(navigationConfig.dashboards, userContext.role),
    [userContext.role]
  );

  // Get current dashboard
  const currentDashboard = useMemo(() => {
    if (preferredDashboardId) {
      return availableDashboards.find(d => d.id === preferredDashboardId);
    }
    return defaultDashboard;
  }, [preferredDashboardId, availableDashboards, defaultDashboard]);

  // Build path helper
  const buildPath = (path: string) => 
    buildTenantPath(path, tenantSlug, userContext.role === 'system-admin');

  return {
    navigationGroups,
    availableDashboards,
    currentDashboard,
    defaultDashboard,
    tenantSlug,
    buildPath
  };
};